import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X, Boxes, Box, Globe, CreditCard, Truck, ShoppingBag, Terminal, MessageSquare, BookOpen, Newspaper } from 'lucide-react';

export default function OlistHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuData = {
    'Produtos': [
      { name: 'Sistema ERP', desc: 'Gestão completa para sua operação', icon: <Box className="w-5 h-5" />, color: 'bg-purple-50 text-[#7f00ff]', path: '/erp' },
      { name: 'Sistema PDV', desc: 'Venda física com gestão integrada', icon: <Terminal className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600', path: '/pdv' },
      { name: 'Hub de Integração', desc: 'Conecte-se aos maiores marketplaces', icon: <Boxes className="w-5 h-5" />, color: 'bg-indigo-50 text-indigo-600', path: '/lukos' },
      { name: 'Envios', desc: 'Logística e fretes inteligentes', icon: <Truck className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600', path: '/lukos' },
      { name: 'Conta Digital', desc: 'Finanças pensadas para o lojista', icon: <CreditCard className="w-5 h-5" />, color: 'bg-cyan-50 text-cyan-600', path: '/lukos' },
      { name: 'E-commerce', desc: 'Sua loja virtual de alta conversão', icon: <ShoppingBag className="w-5 h-5" />, color: 'bg-rose-50 text-rose-600', path: '/lukos' },
    ],
    'Soluções': [
      { name: 'Vender no Mercado Livre', desc: 'Venda nos maiores canais da AL', icon: <Globe className="w-5 h-5" />, color: 'bg-blue-50 text-blue-600', path: '/lukos' },
      { name: 'Vender na Amazon', desc: 'Alcance milhões de clientes globais', icon: <Globe className="w-5 h-5" />, color: 'bg-orange-50 text-orange-600', path: '/lukos' },
      { name: 'Emissão de Nota Fiscal', desc: 'Automatize suas obrigações fiscais', icon: <Newspaper className="w-5 h-5" />, color: 'bg-slate-50 text-slate-600', path: '/lukos' },
      { name: 'Gestão de Estoque', desc: 'Controle total de entradas e saídas', icon: <Box className="w-5 h-5" />, color: 'bg-purple-50 text-purple-600', path: '/erp' },
      { name: 'Cotação de Frete', desc: 'Melhores preços para entregas', icon: <Truck className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600', path: '/lukos' },
      { name: 'Conciliação Bancária', desc: 'Automatize o financeiro', icon: <CreditCard className="w-5 h-5" />, color: 'bg-green-50 text-green-600', path: '/conciliacao' },
    ],
    'Recursos': [
      { name: 'Blog Lukos', desc: 'Dicas de varejo e gestão', icon: <BookOpen className="w-5 h-5" />, color: 'bg-indigo-50 text-indigo-600', path: '/blog' },
      { name: 'Central de Ajuda', desc: 'Tutoriais e suporte técnico', icon: <MessageSquare className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600', path: '/tutoriais' },
      { name: 'Novidades', desc: 'Últimas atualizações', icon: <Newspaper className="w-5 h-5" />, color: 'bg-rose-50 text-rose-600', path: '/blog' },
    ]
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md py-4' : 'bg-transparent py-6'
    }`}>
      <div className="container mx-auto px-6 max-w-7xl flex items-center justify-between">
        
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 z-50">
          <div className="w-10 h-10 flex items-center justify-center transition-colors">
            <img 
              src="/logo.png" 
              alt="Lukos Logo" 
              className={`w-full h-full object-contain ${isScrolled ? '' : 'brightness-0 invert'}`} 
            />
          </div>
          <span className={`text-2xl font-bold tracking-tight transition-colors ${
            isScrolled ? 'text-slate-900' : 'text-white'
          }`}>
            lukos.
          </span>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 h-full">
          {Object.keys(menuData).map((category) => (
            <div 
              key={category} 
              className="relative py-2 group"
              onMouseEnter={() => setActiveDropdown(category)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <div className={`flex items-center gap-1 font-semibold text-base transition-colors cursor-pointer ${
                isScrolled ? 'text-slate-700 hover:text-[#7f00ff]' : 'text-slate-100 hover:text-white'
              }`}>
                {category}
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === category ? 'rotate-180' : ''}`} />
              </div>
              
              <AnimatePresence>
                {activeDropdown === category && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 pointer-events-auto ${
                      category === 'Recursos' ? 'w-[24rem]' : 'w-[45rem]'
                    }`}
                  >
                    {/* Invisible bridge to keep menu open while moving mouse down */}
                    <div className="absolute -top-4 left-0 w-full h-4 bg-transparent" />
                    
                    <div className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-8 border border-white/20">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-8 text-center">{category}</p>
                      
                      <div className={`grid gap-2 ${category === 'Recursos' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {menuData[category as keyof typeof menuData].map((item, i) => (
                          <Link 
                            to={item.path}
                            key={i} 
                            onClick={() => setActiveDropdown(null)}
                            className="group/item flex items-center gap-4 p-4 rounded-2xl hover:bg-slate-50 transition-all cursor-pointer"
                          >
                            <div className={`p-3 rounded-2xl shadow-sm transition-all group-hover/item:scale-110 ${item.color}`}>
                              {item.icon}
                            </div>
                            <div>
                              <p className="text-slate-900 font-bold group-hover/item:text-[#7f00ff] transition-colors leading-tight">
                                {item.name}
                              </p>
                              <p className="text-[11px] text-slate-500 font-medium mt-0.5">{item.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          <Link to="/planos" className={`font-semibold text-base transition-colors ${
            isScrolled ? 'text-slate-700 hover:text-[#7f00ff]' : 'text-slate-100 hover:text-white'
          }`}>
            Planos
          </Link>
        </nav>
        
        {/* CTAs */}
        <div className="hidden md:flex items-center gap-4">
          <button className={`font-semibold px-4 py-2 transition-colors ${
            isScrolled ? 'text-[#7f00ff] hover:text-[#6a00d6]' : 'text-white hover:text-slate-200'
          }`}>
            Entrar
          </button>
          <button className={`font-semibold px-6 py-2.5 rounded-full transition-all hover:scale-105 ${
            isScrolled ? 'bg-[#7f00ff] text-white hover:bg-[#6a00d6]' : 'bg-white text-[#7f00ff] hover:bg-slate-50'
          }`}>
            Criar conta
          </button>
        </div>
        
        {/* Mobile Menu Toggle */}
        <button 
          className="md:hidden relative z-50 p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X className="w-7 h-7 text-slate-900" />
          ) : (
            <Menu className={`w-7 h-7 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
          )}
        </button>
        
      </div>
      
      {/* Mobile Menu Dropdown */}
      <div className={`fixed inset-0 bg-white z-40 transition-transform duration-300 transform ${
        mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
      } pt-24 px-6 md:hidden overflow-y-auto pb-12`}>
        <div className="flex flex-col gap-8">
          {Object.keys(menuData).map((category) => (
            <div key={category} className="flex flex-col gap-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{category}</p>
              <div className="flex flex-col gap-6 pl-2">
                {menuData[category as keyof typeof menuData].map((item, i) => (
                  <Link 
                    to={item.path} 
                    key={i}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-4"
                  >
                    <div className="p-2 rounded-lg bg-slate-50">{item.icon}</div>
                    <span className="text-lg font-bold text-slate-800">{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4 mt-12 pt-8 border-t border-slate-100">
          <button className="w-full text-center py-4 font-bold text-lg text-[#7f00ff] border border-[#7f00ff] rounded-2xl">
            Entrar
          </button>
          <button className="w-full text-center py-4 font-bold text-lg bg-[#7f00ff] text-white rounded-2xl">
            Criar conta
          </button>
        </div>
      </div>
    </header>
  );
}
